/**
 * Agreement document preview + everything the backend knows about that file.
 *
 * The version-history grid's file name opens this instead of punching the blob
 * out to a new tab (the "displayed files are always clickable + previewable"
 * convention). Left pane is the rendered document; the right rail is the parsed
 * side of the record — CorrespondentDocument carries `extractedText` (what the
 * agreement diff runs on) and `indexedTerms` (the MLPA term-indexing agent's
 * structured servicing / fee / early-payment-default output, each row carrying
 * source provenance back into the PDF). Both already come down on the
 * /agreements/versions payload, so there is nothing extra to fetch.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";

import CloseRounded from "@mui/icons-material/CloseRounded";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";

import Flx from "../../../components/layout/Flx";
import {
  useCorrespondentsApi,
  oidOf,
} from "../../../hooks/useCorrespondentsHook";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import formatDollar from "../../../_src_shared/utils/functions/formatDollar";
import { formatPercent } from "../../../utils/numbers/formatPercent";
import { T } from "../../SupportScreen/components/supportUi";
import { SoftBadge, StatusChip, fmtSize } from "./correspondentUi";

const fmtStamp = (v) => (v ? formatDateTime(v, "M/d/yyyy h:mm a") || "—" : "—");

// A fee states EITHER a flat dollar, a percent, or a formula in words.
const feeAmount = (f) => {
  if (f?.amount != null) return formatDollar(f.amount, "—");
  if (f?.percent != null) return formatPercent(f.percent);
  return f?.calculation || "—";
};

const epdTitle = (t) => {
  const scope = t?.productScope || "All Products";
  if (t?.hasEpdClause === false) return `${scope} — no EPD clause`;
  const n = t?.triggerPaymentCount;
  return n ? `${scope} — first ${n} payment${n === 1 ? "" : "s"}` : scope;
};

/* ------------------------------ rail primitives ---------------------------- */

function RailSection({ title, children }) {
  return (
    <Box sx={{ borderBottom: `1px solid ${T.line}`, px: 2.5, py: 2 }}>
      <Box
        sx={{
          mb: 1.25,
          fontSize: "11px",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: T.ink500,
        }}
      >
        {title}
      </Box>
      {children}
    </Box>
  );
}

function Row({ label, value, mono }) {
  return (
    <Flx jb g={2} sx={{ alignItems: "flex-start", py: 0.6, minWidth: 0 }}>
      <Box sx={{ fontSize: "12.5px", color: T.ink300, flexShrink: 0 }}>
        {label}
      </Box>
      <Box
        sx={{
          fontSize: "12.5px",
          fontWeight: 500,
          color: T.ink700,
          textAlign: "right",
          minWidth: 0,
          overflowWrap: "anywhere",
          fontFamily: mono
            ? "ui-monospace, SFMono-Regular, monospace"
            : undefined,
        }}
      >
        {value || "—"}
      </Box>
    </Flx>
  );
}

function Prose({ children }) {
  return (
    <Box
      sx={{
        fontSize: "12.5px",
        lineHeight: 1.6,
        color: T.ink700,
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
      }}
    >
      {children}
    </Box>
  );
}

function EmptyRail({ children }) {
  return (
    <Box
      sx={{ px: 2.5, py: 4, fontSize: "12.5px", color: T.ink300, lineHeight: 1.7 }}
    >
      {children}
    </Box>
  );
}

// Every indexed term is auditable back to the executed agreement — the agent
// PROPOSES, a human confirms, and the verbatim clause is the evidence.
function Provenance({ term }) {
  const cite = [
    term?.sourceParagraph,
    term?.sourcePage ? `p. ${term.sourcePage}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  if (!cite && !term?.sourceText && !term?.confirmedBy) return null;
  return (
    <Box sx={{ mt: 1.25, pt: 1.25, borderTop: `1px dashed ${T.line}` }}>
      {cite && (
        <Box
          sx={{ fontSize: "11px", fontWeight: 700, color: T.ink500, mb: 0.5 }}
        >
          {cite}
        </Box>
      )}
      {term?.sourceText && (
        <Box
          sx={{
            fontSize: "11.5px",
            lineHeight: 1.6,
            color: T.ink500,
            fontStyle: "italic",
            borderLeft: `2px solid ${T.line}`,
            pl: 1.25,
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
          }}
        >
          {term.sourceText}
        </Box>
      )}
      <Box sx={{ mt: 0.75, fontSize: "11px", color: T.ink300 }}>
        {term?.confirmedBy
          ? `Confirmed by ${term.confirmedBy}${
              term?.confirmedDate ? ` · ${fmtStamp(term.confirmedDate)}` : ""
            }`
          : "Proposed by the indexing agent — not yet confirmed"}
      </Box>
    </Box>
  );
}

function TermCard({ title, badge, children }) {
  return (
    <Box
      sx={{
        border: `1px solid ${T.line}`,
        borderRadius: "10px",
        p: 1.5,
        mb: 1.25,
        minWidth: 0,
      }}
    >
      <Flx ac jb g={1} sx={{ mb: 0.75 }}>
        <Box
          sx={{
            fontSize: "12.5px",
            fontWeight: 700,
            color: T.ink900,
            minWidth: 0,
          }}
        >
          {title}
        </Box>
        {badge}
      </Flx>
      {children}
    </Box>
  );
}

/* ----------------------------------- modal --------------------------------- */

export default function AgreementDocumentModal({
  open,
  onClose,
  doc,
  companyId,
  versions = [],
}) {
  const correspondentsApi = useCorrespondentsApi();
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);

  const docOid = oidOf(doc);
  const contentType = doc?.content_type || "";
  const isPdf = contentType === "application/pdf";
  const isImage = contentType.startsWith("image/");
  const displayName =
    doc?.file_display_name || doc?.file_name_as_loaded || "Untitled";

  const indexed = doc?.indexedTerms || null;
  const fees = indexed?.fees?.filter(Boolean) || [];
  const epdTerms = indexed?.epdTerms?.filter(Boolean) || [];
  const repurchase = indexed?.repurchaseTriggers?.filter(Boolean) || [];
  const keyTerms = indexed?.keyTerms?.filter(Boolean) || [];
  const hasIndexed =
    !!indexed &&
    !!(
      indexed.summary ||
      indexed.servicingTerms ||
      indexed.earlyPaymentDefault ||
      fees.length ||
      epdTerms.length ||
      repurchase.length ||
      keyTerms.length
    );
  const extracted = doc?.extractedText || "";

  const supersedes = useMemo(() => {
    const id = doc?.supersedesDocumentId;
    if (!id) return null;
    return versions.find((v) => String(oidOf(v)) === String(id)) || null;
  }, [doc, versions]);

  // Blob-backed preview: the download route streams the bytes, so pull it once
  // per open and hand the object URL to the iframe.
  useEffect(() => {
    if (!open || !companyId || !docOid) return undefined;
    let cancelled = false;
    let url = null;
    setLoading(true);
    setError(null);
    setTab(0);
    correspondentsApi
      .downloadDocument(companyId, docOid, { inline: true })
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(
          blob instanceof Blob ? blob : new Blob([blob], { type: contentType }),
        );
        setPreviewUrl(url);
      })
      .catch((e) => {
        if (!cancelled)
          setError(
            e?.response?.data?.detail || e?.message || "Failed to load the file",
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      setPreviewUrl(null);
      if (url) URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, companyId, docOid]);

  const download = useCallback(() => {
    if (!previewUrl) return;
    const a = document.createElement("a");
    a.href = previewUrl;
    a.download = displayName;
    a.click();
  }, [previewUrl, displayName]);

  const openInNewTab = useCallback(() => {
    if (previewUrl) window.open(previewUrl, "_blank", "noopener");
  }, [previewUrl]);

  if (!doc) return null;

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: { height: "92vh", borderRadius: "14px", overflow: "hidden" },
      }}
    >
      <Flx
        ac
        jb
        g={2}
        sx={{
          px: 2.5,
          py: 1.5,
          borderBottom: `1px solid ${T.line}`,
          minWidth: 0,
        }}
      >
        <Flx ac g={1.25} sx={{ minWidth: 0 }}>
          <DescriptionOutlined sx={{ fontSize: 18, color: T.ink300 }} />
          <Box
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              color: T.ink900,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {displayName}
          </Box>
          {doc?.agreementVersion ? (
            <SoftBadge tone="slate">v{doc.agreementVersion}</SoftBadge>
          ) : null}
          <StatusChip status={doc?.status} />
          {doc?.isCurrentAgreement && (
            <SoftBadge tone="green">Agreement of record</SoftBadge>
          )}
        </Flx>
        <Flx ac g={0.5} sx={{ flexShrink: 0 }}>
          <Tooltip title="Download">
            <span>
              <IconButton
                onClick={download}
                disabled={!previewUrl}
                aria-label="download"
              >
                <FileDownloadOutlined sx={{ fontSize: 18, color: T.ink500 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Open in a new tab">
            <span>
              <IconButton
                onClick={openInNewTab}
                disabled={!previewUrl}
                aria-label="open in new tab"
              >
                <OpenInNewRounded sx={{ fontSize: 18, color: T.ink500 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton onClick={onClose} aria-label="close">
              <CloseRounded sx={{ fontSize: 18, color: T.ink500 }} />
            </IconButton>
          </Tooltip>
        </Flx>
      </Flx>

      <Flx sx={{ flex: 1, minHeight: 0 }}>
        <Box
          sx={{ flex: 1, minWidth: 0, background: T.bg, position: "relative" }}
        >
          {loading && (
            <Flx center sx={{ height: "100%" }}>
              <CircularProgress size={26} />
            </Flx>
          )}
          {!loading && error && (
            <Flx center sx={{ height: "100%", px: 4 }}>
              <Box sx={{ fontSize: 13, color: "#b91c1c", textAlign: "center" }}>
                {error}
              </Box>
            </Flx>
          )}
          {!loading && !error && previewUrl && isPdf && (
            <Box
              component="iframe"
              title={displayName}
              src={previewUrl}
              sx={{ width: "100%", height: "100%", border: "none" }}
            />
          )}
          {!loading && !error && previewUrl && isImage && (
            <Flx center sx={{ height: "100%", p: 3 }}>
              <Box
                component="img"
                alt={displayName}
                src={previewUrl}
                sx={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            </Flx>
          )}
          {!loading && !error && previewUrl && !isPdf && !isImage && (
            <Flx center column g={2} sx={{ height: "100%", px: 4 }}>
              <Box sx={{ fontSize: 13, color: T.ink500, textAlign: "center" }}>
                {contentType || "This file type"} cannot be rendered inline.
              </Box>
              <Button variant="contained" onClick={download}>
                Download {displayName}
              </Button>
            </Flx>
          )}
        </Box>

        {/* parsed / related information rail */}
        <Box
          sx={{
            width: 420,
            flexShrink: 0,
            borderLeft: `1px solid ${T.line}`,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            background: "#fff",
          }}
        >
          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            variant="fullWidth"
            sx={{ borderBottom: `1px solid ${T.line}`, minHeight: 42 }}
          >
            <Tab label="Details" sx={{ minHeight: 42, fontSize: 12.5 }} />
            <Tab
              label={
                fees.length + epdTerms.length
                  ? `Indexed terms (${fees.length + epdTerms.length})`
                  : "Indexed terms"
              }
              sx={{ minHeight: 42, fontSize: 12.5 }}
            />
            <Tab label="Extracted text" sx={{ minHeight: 42, fontSize: 12.5 }} />
          </Tabs>

          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            {tab === 0 && (
              <>
                <RailSection title="Version">
                  <Row label="Category" value={doc?.category} />
                  <Row
                    label="Version"
                    value={
                      doc?.agreementVersion ? `v${doc.agreementVersion}` : "—"
                    }
                  />
                  <Row label="Stage" value={doc?.agreementStage} />
                  <Row label="Status" value={doc?.status} />
                  <Row
                    label="Supersedes"
                    value={
                      supersedes
                        ? `v${supersedes.agreementVersion} · ${
                            supersedes.file_display_name || "—"
                          }`
                        : "—"
                    }
                  />
                </RailSection>

                <RailSection title="Audit trail">
                  <Row label="Uploaded" value={fmtStamp(doc?.uploadDate)} />
                  <Row label="Uploaded by" value={doc?.uploadedBy} />
                  <Row label="Reviewed" value={fmtStamp(doc?.reviewedDate)} />
                  <Row label="Reviewed by" value={doc?.reviewedBy} />
                  <Row label="Approved" value={fmtStamp(doc?.approvedDate)} />
                  <Row label="Approved by" value={doc?.approvedBy} />
                </RailSection>

                <RailSection title="File">
                  <Row label="Original name" value={doc?.file_name_as_loaded} />
                  <Row label="Type" value={contentType} />
                  <Row label="Size" value={fmtSize(doc?.size)} />
                  <Row label="SHA-1" value={doc?.sha1} mono />
                </RailSection>

                {(doc?.diffSummary || doc?.notes) && (
                  <RailSection title="Notes">
                    {doc?.diffSummary && (
                      <Box sx={{ mb: doc?.notes ? 1.5 : 0 }}>
                        <Box
                          sx={{
                            fontSize: "11.5px",
                            fontWeight: 700,
                            color: T.ink500,
                            mb: 0.5,
                          }}
                        >
                          What changed vs the prior version
                        </Box>
                        <Prose>{doc.diffSummary}</Prose>
                      </Box>
                    )}
                    {doc?.notes && <Prose>{doc.notes}</Prose>}
                  </RailSection>
                )}
              </>
            )}

            {tab === 1 &&
              (hasIndexed ? (
                <>
                  {indexed.summary && (
                    <RailSection title="Summary">
                      <Prose>{indexed.summary}</Prose>
                    </RailSection>
                  )}
                  {indexed.servicingTerms && (
                    <RailSection title="Servicing">
                      <Prose>{indexed.servicingTerms}</Prose>
                    </RailSection>
                  )}
                  {fees.length > 0 && (
                    <RailSection title={`Fees (${fees.length})`}>
                      {fees.map((f, i) => (
                        <TermCard
                          key={f?.oid || i}
                          title={f?.name || "Unnamed fee"}
                          badge={
                            <SoftBadge tone="slate">{feeAmount(f)}</SoftBadge>
                          }
                        >
                          {f?.description && <Prose>{f.description}</Prose>}
                          <Row label="Ownership" value={f?.ownership} />
                          {f?.ownership === "Split" && (
                            <>
                              <Row
                                label="Correspondent share"
                                value={
                                  f?.correspondentSplitPercent != null
                                    ? formatPercent(f.correspondentSplitPercent)
                                    : "—"
                                }
                              />
                              <Row
                                label="Loan buyer share"
                                value={
                                  f?.loanBuyerSplitPercent != null
                                    ? formatPercent(f.loanBuyerSplitPercent)
                                    : "—"
                                }
                              />
                            </>
                          )}
                          <Provenance term={f} />
                        </TermCard>
                      ))}
                    </RailSection>
                  )}
                  {epdTerms.length > 0 && (
                    <RailSection
                      title={`Early payment default (${epdTerms.length})`}
                    >
                      {epdTerms.map((t, i) => (
                        <TermCard
                          key={t?.oid || i}
                          title={epdTitle(t)}
                          badge={
                            t?.variation ? (
                              <SoftBadge
                                tone={
                                  t.variation === "Distinctly Unique"
                                    ? "amber"
                                    : "slate"
                                }
                              >
                                {t.variation}
                              </SoftBadge>
                            ) : null
                          }
                        >
                          {t?.triggerText && <Prose>{t.triggerText}</Prose>}
                          <Row label="Trigger basis" value={t?.triggerBasis} />
                          <Row
                            label="Cure"
                            value={
                              t?.cureDays != null ? `${t.cureDays} days` : "—"
                            }
                          />
                          {t?.cureCarveOut && (
                            <Row label="Cure carve-out" value={t.cureCarveOut} />
                          )}
                          <Row
                            label="Repurchase window"
                            value={
                              t?.repurchaseWindowDays != null
                                ? `${t.repurchaseWindowDays} ${
                                    t?.repurchaseWindowBusinessDays === false
                                      ? "calendar"
                                      : "business"
                                  } days`
                                : "—"
                            }
                          />
                          <Row label="Window starts" value={t?.windowStart} />
                          {t?.repurchasePeriodDays != null && (
                            <Row
                              label="Repurchase period"
                              value={`${t.repurchasePeriodDays} days`}
                            />
                          )}
                          {t?.remedy && <Row label="Remedy" value={t.remedy} />}
                          {t?.premiumRecapture && (
                            <Row
                              label="Premium recapture"
                              value={t.premiumRecapture}
                            />
                          )}
                          <Provenance term={t} />
                        </TermCard>
                      ))}
                    </RailSection>
                  )}
                  {indexed.earlyPaymentDefault && !epdTerms.length && (
                    <RailSection title="Early payment default">
                      <Prose>{indexed.earlyPaymentDefault}</Prose>
                    </RailSection>
                  )}
                  {repurchase.length > 0 && (
                    <RailSection
                      title={`Repurchase triggers (${repurchase.length})`}
                    >
                      {repurchase.map((r, i) => (
                        <Box key={i} sx={{ mb: 0.75 }}>
                          <Prose>
                            {typeof r === "string" ? r : JSON.stringify(r)}
                          </Prose>
                        </Box>
                      ))}
                    </RailSection>
                  )}
                  {keyTerms.length > 0 && (
                    <RailSection title={`Key terms (${keyTerms.length})`}>
                      {keyTerms.map((k, i) =>
                        typeof k === "string" ? (
                          <Box key={i} sx={{ mb: 0.75 }}>
                            <Prose>{k}</Prose>
                          </Box>
                        ) : (
                          <TermCard
                            key={i}
                            title={k?.term || "Term"}
                            badge={
                              k?.section ? (
                                <SoftBadge tone="slate">{k.section}</SoftBadge>
                              ) : null
                            }
                          >
                            <Prose>{k?.value ?? "—"}</Prose>
                          </TermCard>
                        ),
                      )}
                    </RailSection>
                  )}
                  <Box sx={{ px: 2.5, py: 2, fontSize: "11px", color: T.ink300 }}>
                    Indexed by {indexed.indexedBy || "—"}
                    {indexed.indexedDate
                      ? ` · ${fmtStamp(indexed.indexedDate)}`
                      : ""}
                  </Box>
                </>
              ) : (
                <EmptyRail>
                  No indexed terms on this version yet. The MLPA term-indexing
                  agent reads the executed agreement and writes the negotiated
                  servicing, fee, early-payment-default and repurchase terms onto
                  the document — each with the verbatim clause, page and section
                  it came from — so they are queryable without re-reading the
                  PDF. The agent proposes; a human confirms.
                </EmptyRail>
              ))}

            {tab === 2 &&
              (extracted ? (
                <Box sx={{ p: 2.5 }}>
                  <Box sx={{ fontSize: "11px", color: T.ink300, mb: 1.25 }}>
                    {extracted.length.toLocaleString()} characters · this is the
                    text the version diff is computed against.
                  </Box>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      fontFamily: "ui-monospace, SFMono-Regular, monospace",
                      fontSize: "11.5px",
                      lineHeight: 1.6,
                      color: T.ink700,
                      whiteSpace: "pre-wrap",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {extracted}
                  </Box>
                </Box>
              ) : (
                <EmptyRail>
                  No extracted text for this version yet. The extraction pipeline
                  populates it after upload; until both versions have it, Compare
                  versions reports that extracted text is not yet available.
                </EmptyRail>
              ))}
          </Box>
        </Box>
      </Flx>
    </Dialog>
  );
}
